import { AssetSubClassCode } from '@api/api/entity-constants';
import { type KeyValue } from '@api/api/entity-report';
import { type Entity, PrimaryMethod } from '@api/api/entity-schema';

export type KeyValuesEntity = Pick<Entity, 'sub_class' | 'primary_method'> &
  Partial<Pick<Entity, 'tag'>>;

export const isManufacturingPmi = (key: string, entityTag?: string) =>
  /^[^.]+\.pmi_new_orders\.manufacturing\.[^.]+$/.test(key) &&
  (!entityTag || key.startsWith(`${entityTag}.`));

const isPriceMetricApplicable = (
  key: string,
  entity: KeyValuesEntity,
  keys: string[]
) => entity.primary_method === PrimaryMethod.Price && keys.includes(key);

export const isKeyValueApplicable = (
  { key, value_fmt }: KeyValue,
  entity?: KeyValuesEntity
) => {
  if (!value_fmt?.trim()) {
    return false;
  }
  if (!entity || entity.sub_class === AssetSubClassCode.EquityStock) {
    return true;
  }

  if (
    entity.sub_class === AssetSubClassCode.EcoCentralBank ||
    entity.primary_method === PrimaryMethod.CbPolicyRate
  ) {
    return [
      'central_bank_policy_rate',
      ...(entity.tag ? [`${entity.tag}.central_bank_policy_rate`] : []),
    ].includes(key);
  }

  if (
    [AssetSubClassCode.EcoCountry, AssetSubClassCode.EcoArea].includes(
      entity.sub_class
    )
  ) {
    return (
      [
        'cpi_core_level.change_pct(horiz=1y)',
        'central_bank_policy_rate',
        'bop_current_account_level.change_pct(horiz=1y)',
        'consumer_credit_level.change_pct(horiz=1y)',
        'm0_level.change_pct(horiz=1y)',
        'retail_sales_level.change_pct(horiz=1y)',
        'unemployment_rate',
      ].includes(key) ||
      (isManufacturingPmi(key, entity.tag) &&
        Number.isFinite(Number(value_fmt.replaceAll(',', ''))))
    );
  }

  if (
    [AssetSubClassCode.FiSov, AssetSubClassCode.FiInflationSov].includes(
      entity.sub_class
    ) ||
    entity.primary_method === PrimaryMethod.Yield
  ) {
    // The report does not establish return conventions or MACD units for rates.
    return ['carry(fully_funded=yes)', 'yield_rsi(horiz=14d)'].includes(key);
  }

  if (
    [
      AssetSubClassCode.FutureElectricity,
      AssetSubClassCode.FutureEnergy,
      AssetSubClassCode.FutureFood,
      AssetSubClassCode.FutureGrains,
      AssetSubClassCode.FutureMetals,
      AssetSubClassCode.FutureSofts,
      AssetSubClassCode.CommodityIndex,
      AssetSubClassCode.MajorCross,
      AssetSubClassCode.GenericCross,
      AssetSubClassCode.FutureCurrency,
      AssetSubClassCode.FutureBonds,
      AssetSubClassCode.FutureInterestRate,
    ].includes(entity.sub_class)
  ) {
    return isPriceMetricApplicable(key, entity, [
      'mom(horiz=1m)',
      'mom(horiz=3m)',
      'price_rsi(horiz=14d)',
      'realized_vol(horiz=1m,return_horiz=1d)',
    ]);
  }

  if (
    [AssetSubClassCode.FiEtf, AssetSubClassCode.CommodityEtf].includes(
      entity.sub_class
    )
  ) {
    // These existing fund statistics describe the traded vehicle, not its holdings' economy.
    return isPriceMetricApplicable(key, entity, [
      'net_asset_value',
      'fair_value_discount_etf',
      'dividend_yield',
      'fair_value_seasonality(years=5,fcst_horiz=3m)',
      'implied_vol_atm_interpolated',
      'price_rsi(horiz=14d)',
      'price_mavg_spread(horiz=50d,horiz2=200d)',
      'number_of_shares',
      'mom(horiz=1m)',
      'realized_vol(horiz=1m,return_horiz=1d)',
    ]);
  }

  if (
    [AssetSubClassCode.FiFund, AssetSubClassCode.CommodityFund].includes(
      entity.sub_class
    )
  ) {
    return isPriceMetricApplicable(key, entity, [
      'mom(horiz=6m)',
      'fair_value_seasonality(years=5,fcst_horiz=1m)',
      'fair_value_seasonality(years=5,fcst_horiz=3m)',
      'fair_value_seasonality(years=5,fcst_horiz=6m)',
      'surprise_move(horiz=1w,vol_horiz=3m)',
      'surprise_move(horiz=1m,vol_horiz=3m)',
      'sharpe_ratio(horiz=1w,vol_horiz=3m)',
      'sharpe_ratio(horiz=1m,vol_horiz=3m)',
    ]);
  }

  return true;
};
